import { useEffect, useState } from 'react';

export interface MetaParams {
  /** Facebook Click ID, viene en `?fbclid=...` cuando el usuario llega desde un anuncio Meta. */
  fbclid?: string | null;
  /** Facebook Browser ID (cookie `_fbp`). Lo setea el Meta Pixel. */
  fbp?: string | null;
  /** Facebook Click cookie (`_fbc`), formato `fb.1.<unix_ms>.<fbclid>`. La seteamos al recibir fbclid. */
  fbc?: string | null;
  /** Event ID compartido entre browser pixel y CAPI server-side para dedupe. */
  meta_event_id?: string | null;
  /** Atribución directa por URL params (Meta a veces los mete como `&ad_id=...`). */
  ad_id?: string | null;
  adset_id?: string | null;
  campaign_id?: string | null;
  /** Plataforma origen inferida del UTM (`fb`, `ig`, `tiktok`...). */
  platform?: string | null;
}

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]+)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function setFbcCookie(fbclid: string): string {
  const fbc = `fb.1.${Date.now()}.${fbclid}`;
  document.cookie = `_fbc=${fbc};max-age=7776000;path=/;SameSite=Lax`;
  return fbc;
}

function getOrCreateMetaEventId(): string {
  const KEY = 'meta_event_id';
  // sessionStorage para que el id viva durante la visita y se reuse en
  // browser pixel + server CAPI (mismo id → Meta deduplica el evento).
  let id = sessionStorage.getItem(KEY);
  if (!id) {
    id =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    sessionStorage.setItem(KEY, id);
  }
  return id;
}

function inferPlatform(urlParams: URLSearchParams): string | null {
  const utmSource = (urlParams.get('utm_source') || '').toLowerCase();
  if (urlParams.get('fbclid')) return urlParams.get('platform') || 'fb';
  if (utmSource.includes('instagram') || utmSource === 'ig') return 'ig';
  if (utmSource.includes('facebook') || utmSource === 'fb' || utmSource === 'meta') return 'fb';
  if (utmSource.includes('tiktok')) return 'tiktok';
  if (utmSource.includes('youtube') || utmSource === 'yt') return 'youtube';
  return urlParams.get('platform') || null;
}

export function useMetaParams(): MetaParams {
  const [metaParams, setMetaParams] = useState<MetaParams>({});

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const fbclid = urlParams.get('fbclid');

    // Persistir fbclid para sobrevivir navegación entre páginas de la landing.
    if (fbclid) {
      localStorage.setItem('fbclid', fbclid);
      setFbcCookie(fbclid);
    }

    // Cookie _fbp: la pone el Meta Pixel al cargar el script.
    const fbp = getCookie('_fbp');
    if (fbp) localStorage.setItem('fbp', fbp);

    // Cookie _fbc: la creamos arriba si llega fbclid; si no, leer la existente.
    const fbc = getCookie('_fbc');

    // Atribución directa por URL params (Meta puede pasarlos así en algunos casos).
    const adId =
      urlParams.get('ad_id') ||
      urlParams.get('adid') ||
      localStorage.getItem('ad_id');
    const adsetId =
      urlParams.get('adset_id') ||
      urlParams.get('adsetid') ||
      localStorage.getItem('adset_id');
    const campaignId =
      urlParams.get('campaign_id') ||
      urlParams.get('campaignid') ||
      localStorage.getItem('campaign_id');

    if (adId) localStorage.setItem('ad_id', adId);
    if (adsetId) localStorage.setItem('adset_id', adsetId);
    if (campaignId) localStorage.setItem('campaign_id', campaignId);

    setMetaParams({
      fbclid: fbclid || localStorage.getItem('fbclid') || null,
      fbp: fbp || localStorage.getItem('fbp') || null,
      fbc: fbc || null,
      meta_event_id: getOrCreateMetaEventId(),
      ad_id: adId || null,
      adset_id: adsetId || null,
      campaign_id: campaignId || null,
      platform: inferPlatform(urlParams),
    });
  }, []);

  return metaParams;
}
